import { Collapse, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, CommunicationTrackingEventOut, CommunicationTrackingEventType } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../../../common/getDateAndTimeFormat';
import TimeUtils from '../../../../../common/TimeUtils';

type CommunicationTrackingRowProps = {
  event: CommunicationTrackingEventOut;
};

export const CommunicationTrackingRow = ({ event }: CommunicationTrackingRowProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);
  const [expandedSmsBodyShown, setExpandedSmsBodyShown] = useState(false);
  const [expandedWhatsAppBodyShown, setExpandedWhatsAppBodyShown] = useState(false);

  const formatDateTime = (createdAt: string) => {
    const date = TimeUtils.getLocalTime(createdAt, account.timezone);
    return `${date.format(shortDateFormat)} ${date.format(timeFormat)}`;
  };

  const getEmailTemplateTypeDisplay = (templateType: string | null) => {
    if (!templateType) return null;
    return t(`emailTracking.templateTypes.${templateType}`);
  };

  const getSmsTypeDisplay = (smsType: string | null) => {
    if (!smsType) return null;
    return t(`emailTracking.smsTypes.${smsType}`);
  };

  const getWhatsAppTypeDisplay = (templateName: string | null) => {
    if (!templateName) return null;
    if (templateName.includes('confirmation') || templateName.includes('confirm')) {
      return t(`emailTracking.whatsappTypes.confirmation`);
    }
    if (templateName.includes('reminder')) {
      return t(`emailTracking.whatsappTypes.reminder`);
    }
    return '📱 Message';
  };

  const getMediaIcon = () => {
    if (event.event_type?.startsWith('email_')) return '📧';
    if (event.event_type?.startsWith('sms_')) return '📱';
    if (event.event_type?.startsWith('whatsapp_'))
      return <img src="/images/whatsapp.png" width="16" height="16" alt="WhatsApp" />;
    return '📧';
  };

  const getEventDisplay = (eventType: CommunicationTrackingEventType) => {
    return t(`emailTracking.events.${eventType}`);
  };

  const reason = event.sendgrid_email_data?.reason || event.twilio_sms_data?.reason;
  const url = event.sendgrid_email_data?.url;
  const templateType = event.sendgrid_email_data?.email_action_type;
  const smsType = event.twilio_sms_data?.sms_type;
  const smsBody = event.twilio_sms_data?.message_body;
  const whatsappData = event.whatsapp_message_data;
  const whatsappBody = whatsappData?.message_body;
  const whatsappTemplate = whatsappData?.template_name;
  const whatsappError =
    whatsappData?.error_code && whatsappData?.error_title
      ? `${whatsappData.error_code}: ${whatsappData.error_title}`
      : null;

  return (
    <>
      <Grid container gap={1} alignItems="center" py={0.5}>
        <Grid item xs={0.5}>
          <Typography variant="body2">{getMediaIcon()}</Typography>
        </Grid>

        <Grid item xs={2.5}>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(event.created_at)}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          {templateType && <Typography variant="body2">{getEmailTemplateTypeDisplay(templateType)}</Typography>}
          {smsType && <Typography variant="body2">{getSmsTypeDisplay(smsType)}</Typography>}
          {whatsappTemplate && <Typography variant="body2">{getWhatsAppTypeDisplay(whatsappTemplate)}</Typography>}
        </Grid>

        <Grid item xs={2}>
          <Typography variant="body2">{getEventDisplay(event.event_type)}</Typography>
        </Grid>

        <Grid item xs={3}>
          {reason && (
            <Typography variant="body2" color="error" title={reason}>
              {reason.length > 30 ? `${reason.slice(0, 30)}...` : reason}
            </Typography>
          )}
          {whatsappError && (
            <Typography variant="body2" color="error" title={whatsappError}>
              {whatsappError.length > 30 ? `${whatsappError.slice(0, 30)}...` : whatsappError}
            </Typography>
          )}
          {url && (
            <Typography variant="body2" color="primary" title={url}>
              🔗 {t('emailTrackingPanel.linkClicked', 'Link clicked')}
            </Typography>
          )}
          {smsBody && (
            <Grid
              onClick={() => setExpandedSmsBodyShown(!expandedSmsBodyShown)}
              sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline' }}>
              <Typography variant="body2">{t('emailTrackingPanel.viewMessage', 'View Message')}</Typography>
            </Grid>
          )}
          {whatsappBody && (
            <Grid
              onClick={() => setExpandedWhatsAppBodyShown(!expandedWhatsAppBodyShown)}
              sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline' }}>
              <Typography variant="body2">{t('emailTrackingPanel.viewMessage', 'View Message')}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      {smsBody && (
        <Collapse in={expandedSmsBodyShown}>
          <Grid container sx={{ pl: 4 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                "{smsBody}"
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      )}

      {whatsappBody && (
        <Collapse in={expandedWhatsAppBodyShown}>
          <Grid container sx={{ pl: 4 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                "{whatsappBody}"
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      )}
    </>
  );
};
